import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Crown, ArrowUp, ArrowDown, Minus, TrendingUp, BadgeCheck } from "lucide-react";
import {
  fmtUSD, fmtUSDprecise, fmtNum, timeAgo, entityLabel, entityName, entityImage, entityPath,
} from "@/lib/format";
import ClimbPreview, { ClimbOverlay } from "@/components/leaderboard/ClimbPreview";
import { BackedBadge, BoostBackFace, ClimbDelta } from "@/components/leaderboard/BoostSuccessOverlay";
import { hasClimbTargets, visibleClimbTargets } from "@/components/leaderboard/climbTargets";
import { useMediaQuery } from "@/components/leaderboard/useMediaQuery";

const IDLE_BOOST = { status: "idle" };

function RankMovement({ rank, prev }) {
  if (prev == null || prev === rank) {
    return <span className="flex items-center gap-0.5 text-[10px] text-zinc-600"><Minus className="h-3 w-3" strokeWidth={2} /></span>;
  }
  const up = rank < prev;
  return (
    <span className={`flex items-center gap-0.5 text-[10px] font-bold ${up ? "text-positive" : "text-negative"}`}>
      {up ? <ArrowUp className="h-3 w-3" strokeWidth={2.5} /> : <ArrowDown className="h-3 w-3" strokeWidth={2.5} />}
      {Math.abs(prev - rank)}
    </span>
  );
}

function EntityAvatar({ entity }) {
  const img = entityImage(entity);
  const label = entityLabel(entity);
  if (img) {
    return <img src={img} alt={label} className="h-9 w-9 rounded-full border border-white/10 object-cover" />;
  }
  return (
    <div className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-gradient-to-br from-zinc-800 to-zinc-900 font-display text-xs font-black">
      {String(
        (entity.category === "tokens" ? entity.ticker : entity.display_name) || "?"
      ).slice(0, 2).toUpperCase()}
    </div>
  );
}

function Row({
  entity,
  onBack,
  climbEnabled,
  boost,
  dimmed,
  ghostTarget,
  inClimbPath,
  climbDelta,
  reducedMotion,
  pointerHover,
  onPreview,
  onClearPreview,
  onSelectTarget,
  onHoverTarget,
  registerRow,
}) {
  const navigate = useNavigate();
  const rank = entity.leaderboard_rank || entity.rank;
  const isTop = rank === 1;
  const change24 = entity.amount_24h || 0;
  const label = entityLabel(entity);
  const targets = entity.climb_targets || [];
  const canClimb = climbEnabled && hasClimbTargets(entity);
  const isPreview = canClimb && boost.status === "previewing" && boost.coinId === entity.id;
  const isSuccess = boost.status === "success" && boost.coinId === entity.id;
  const showBack = isSuccess && boost.phase === "flip";
  const isClimbing = isSuccess && boost.phase === "climb";
  const isGhostTarget = Boolean(ghostTarget);

  const openPreview = (event) => {
    event.stopPropagation();
    if (isPreview && !pointerHover) onClearPreview(entity.id);
    else onPreview(entity.id);
  };

  return (
    <motion.div
      ref={(node) => registerRow(entity.id, node)}
      layout
      layoutId={entity.id}
      data-testid={`leaderboard-row-${entity.slug}`}
      data-rank={rank}
      transition={reducedMotion ? { duration: 0 } : { type: "spring", stiffness: 300, damping: 30 }}
      onClick={(event) => {
        if (event.target.closest("button, a")) return;
        navigate(entityPath(entity));
      }}
      onMouseEnter={() => {
        if (!pointerHover || !canClimb) return;
        if (boost.status === "previewing" && boost.coinId !== entity.id) return;
        onPreview(entity.id);
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape" && isPreview) {
          event.stopPropagation();
          onClearPreview(entity.id);
        }
      }}
      style={{ zIndex: isPreview || isSuccess ? 30 : 1, position: "relative", perspective: 900 }}
      className={`group border-b border-white/5 px-3 py-3 transition-colors sm:px-4 ${
        isTop && !isPreview && !isSuccess ? "bg-gold/[0.04]" : ""
      } ${isPreview ? "climb-active-row bg-cyan-400/[0.06]" : ""} ${
        isGhostTarget || inClimbPath ? "ring-1 ring-inset ring-cyan-300/40" : ""
      } ${!isPreview && !isGhostTarget ? "hover:bg-white/[0.03]" : ""} ${
        isClimbing ? "climb-success-glow climb-active-row" : ""
      } ${dimmed ? "opacity-60" : ""}`}
    >
      {isClimbing && <span aria-hidden="true" className="climb-trail pointer-events-none absolute bottom-1 left-6 top-1 w-8 rounded-full border-l-2 border-cyan-300/80" />}
      {isClimbing && (
        <div className="pointer-events-none absolute left-[42%] top-1/2 z-40 -translate-y-1/2">
          <BackedBadge amount={boost.amount} slug={entity.slug} tilted />
        </div>
      )}
      <motion.div
        animate={{
          scale: isPreview && pointerHover && !reducedMotion ? 1.02 : 1,
          rotateY: showBack ? 180 : 0,
        }}
        transition={{
          scale: { duration: reducedMotion ? 0 : 0.18 },
          rotateY: { duration: showBack && !reducedMotion ? 0.62 : 0 },
        }}
        style={{ transformStyle: "preserve-3d", WebkitTransformStyle: "preserve-3d" }}
        className="relative"
      >
        <div
          className="grid cursor-pointer grid-cols-[36px_1fr_auto] items-center gap-3 sm:grid-cols-[56px_minmax(0,3fr)_minmax(0,1.4fr)_minmax(0,1.2fr)_minmax(0,1.6fr)_auto] sm:gap-4"
          style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
        >
          <div className="flex flex-col items-center">
            <div className={`flex items-center gap-1 font-mono text-lg font-black sm:text-xl ${isTop ? "text-gold" : "text-zinc-500"} ${isClimbing ? "climb-rank-pulse text-cyan-200" : ""}`}>
              {isTop && <Crown className="h-4 w-4 fill-gold text-gold sm:h-5 sm:w-5" strokeWidth={1.5} />}
              {rank}
            </div>
            <RankMovement rank={rank} prev={entity.previous_rank} />
            {climbDelta != null && <ClimbDelta value={climbDelta} slug={entity.slug} />}
          </div>

          <div className="flex min-w-0 items-center gap-3">
            <Link to={entityPath(entity)} onClick={(event) => event.stopPropagation()} className="flex min-w-0 items-center gap-3">
            <EntityAvatar entity={entity} />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate font-display text-base font-extrabold tracking-tight">{label}</span>
                {entity.category === "callers" && entity.verified && (
                  <BadgeCheck className="h-4 w-4 shrink-0 text-sky-400" data-testid="verified-badge-row" />
                )}
                {entity.category === "callers" && !entity.verified && (
                  <span className="rounded border border-white/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-zinc-500">Unclaimed</span>
                )}
              </div>
              <div className="truncate text-xs text-zinc-500">{entityName(entity)}</div>
              {entity.gap_to_next != null && (
                <div className="mt-0.5 hidden text-[11px] font-mono text-gold/80 sm:block">
                  {fmtUSD(entity.gap_to_next)} to take #{rank - 1}
                </div>
              )}
            </div>
            </Link>
            <div className="min-w-0">
              {canClimb && (
                <button
                  type="button"
                  data-testid={`climb-button-${entity.slug}`}
                  aria-expanded={isPreview}
                  aria-label={`Show climb targets for ${label}`}
                  onClick={openPreview}
                  className="mt-1 rounded-full border border-cyan-300/40 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-cyan-200 hover:bg-cyan-400/10 sm:sr-only sm:focus:not-sr-only sm:focus:absolute sm:focus:z-40"
                >
                  Climb
                </button>
              )}
            </div>
          </div>

          <div className="text-right sm:hidden">
            <div className="font-mono text-sm font-bold text-white">{fmtUSD(entity.total_backed_usd)}</div>
            <button
              data-testid={`back-button-mobile-${entity.slug}`}
              onClick={(event) => { event.stopPropagation(); onBack(entity); }}
              className="mt-1 rounded-full bg-white px-3 py-1 text-[11px] font-bold text-black active:scale-95"
            >
              BACK
            </button>
          </div>

          <div className="hidden text-right sm:block">
            <div className="font-mono text-lg font-bold text-white">{fmtUSD(entity.total_backed_usd)}</div>
            {entity.trend_volume != null && (
              <div className="text-[11px] font-mono text-zinc-500">{fmtUSD(entity.trend_volume)} vol</div>
            )}
          </div>
          <div className="hidden text-center sm:block">
            <span className="flex items-center justify-center gap-1 font-mono text-sm text-positive">
              <TrendingUp className="h-3 w-3" strokeWidth={2} />+{fmtUSD(change24)}
            </span>
            <div className="text-[11px] text-zinc-600">today</div>
          </div>
          <div className="hidden text-right sm:block">
            <div className="font-mono text-sm text-zinc-300">{fmtNum(entity.contributors)}</div>
            <div className="text-[11px] text-zinc-600">supporters</div>
            {entity.last_contribution && (
              <div className="mt-0.5 text-[10px] font-mono text-zinc-600">
                {fmtUSDprecise(entity.last_contribution.usd_value)} · {timeAgo(entity.last_contribution.created_at)}
              </div>
            )}
          </div>
          <div className="hidden sm:block">
            <button
              data-testid={`back-button-${entity.slug}`}
              onClick={(event) => { event.stopPropagation(); onBack(entity); }}
              className="rounded-full bg-white px-4 py-2 text-sm font-bold text-black transition-transform hover:bg-zinc-200 active:scale-95"
            >
              BACK
            </button>
          </div>
        </div>
        {showBack && <BoostBackFace amount={boost.amount} />}
      </motion.div>

      {isPreview && !pointerHover && (
        <ClimbPreview
          key={entity.id}
          label={label}
          targets={targets}
          activeTargetRank={null}
          onHoverTarget={onHoverTarget}
          onSelectTarget={(target) => onSelectTarget(entity, target)}
          onClose={() => onClearPreview(entity.id)}
        />
      )}
    </motion.div>
  );
}

export default function LeaderboardTable({
  entities,
  onBack,
  climbEnabled = false,
  boost = IDLE_BOOST,
  climbDeltaMap = {},
  onPreview = () => {},
  onClearPreview = () => {},
  onSelectTarget = () => {},
}) {
  const containerRef = useRef(null);
  const rowNodes = useRef(new Map());
  const [markers, setMarkers] = useState(null);
  const [activeTargetRank, setActiveTargetRank] = useState(null);
  const pointerHover = useMediaQuery("(hover: hover) and (min-width: 640px)");
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const previewing = climbEnabled && boost.status === "previewing";
  const succeeding = boost.status === "success";

  useEffect(() => {
    setActiveTargetRank(null);
  }, [boost.status, boost.coinId]);

  const registerRow = useCallback((id, node) => {
    if (node) rowNodes.current.set(id, node);
    else rowNodes.current.delete(id);
  }, []);

  useLayoutEffect(() => {
    if (!pointerHover || !previewing) {
      setMarkers(null);
      return undefined;
    }
    const measure = () => {
      const container = containerRef.current;
      const source = rowNodes.current.get(boost.coinId);
      const climber = entities.find((entity) => entity.id === boost.coinId);
      if (!container || !source || !climber) {
        setMarkers(null);
        return;
      }
      const origin = container.getBoundingClientRect().top;
      const sourceBox = source.getBoundingClientRect();
      const marks = visibleClimbTargets(climber.climb_targets, false).map((target) => {
        const rowEntity = entities.find((entity) => (entity.leaderboard_rank || entity.rank) === target.target_rank);
        const node = rowEntity && rowNodes.current.get(rowEntity.id);
        if (!node) return null;
        const box = node.getBoundingClientRect();
        return { target, top: box.top - origin + box.height / 2 };
      }).filter(Boolean);
      const next = {
        sourceTop: sourceBox.top - origin + sourceBox.height / 2,
        image: entityImage(climber),
        label: entityLabel(climber),
        marks,
      };
      setMarkers((prev) => {
        if (prev && prev.label === next.label && prev.marks.length === next.marks.length && prev.sourceTop === next.sourceTop) {
          return prev;
        }
        return next;
      });
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [pointerHover, previewing, boost.coinId, entities]);

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#080808]"
      data-testid="leaderboard-table"
      onMouseLeave={() => {
        if (pointerHover && previewing) onClearPreview(boost.coinId);
      }}
    >
      <div className="hidden grid-cols-[56px_minmax(0,3fr)_minmax(0,1.4fr)_minmax(0,1.2fr)_minmax(0,1.6fr)_auto] gap-4 border-b border-white/10 px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-zinc-600 sm:grid">
        <div className="text-center">Rank</div>
        <div>Token / Person</div>
        <div className="text-right">Total Backed</div>
        <div className="text-center">24h</div>
        <div className="text-right">Supporters</div>
        <div className="text-right">Action</div>
      </div>
      {pointerHover && !reducedMotion && (
        <ClimbOverlay
          markers={markers}
          activeTargetRank={activeTargetRank}
          onHoverTarget={(target) => setActiveTargetRank(target ? target.target_rank : null)}
          onSelectTarget={(target) => {
            const climber = entities.find((entity) => entity.id === boost.coinId);
            if (climber) onSelectTarget(climber, target);
          }}
        />
      )}
      <AnimatePresence>
        {entities.map((entity) => (
          <Row
            key={entity.id}
            entity={entity}
            onBack={onBack}
            climbEnabled={climbEnabled}
            boost={boost}
            dimmed={(previewing || succeeding) && boost.coinId !== entity.id}
            ghostTarget={previewing && activeTargetRank != null && (entity.leaderboard_rank || entity.rank) === activeTargetRank}
            inClimbPath={previewing && (markers?.marks || []).some((mark) => mark.target.target_rank === (entity.leaderboard_rank || entity.rank))}
            climbDelta={climbDeltaMap[entity.id]}
            reducedMotion={reducedMotion}
            pointerHover={pointerHover}
            onPreview={onPreview}
            onClearPreview={onClearPreview}
            onSelectTarget={onSelectTarget}
            onHoverTarget={(target) => setActiveTargetRank(target ? target.target_rank : null)}
            registerRow={registerRow}
          />
        ))}
      </AnimatePresence>
      {entities.length === 0 && (
        <div className="px-4 py-16 text-center text-zinc-500" data-testid="leaderboard-empty">
          Nothing here yet. Add the first one and take #1.
        </div>
      )}
    </div>
  );
}
